import { useState, MouseEvent } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { Combobox } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import cx from 'classnames';

import { GetServerPageProps } from '../lib/apollo';
import { getServerPageLocationSearch } from './LocationSearch.generated';

type Location = NonNullable<
  GetServerPageProps<
    typeof getServerPageLocationSearch
  >['data']['countyCollection']
>['items'][0];

interface LocationSearch {
  data: GetServerPageProps<typeof getServerPageLocationSearch>['data'];
  linkPrefix: string;
}

export const LocationSearch: React.FC<LocationSearch> = ({
  data,
  linkPrefix,
}) => {
  const router = useRouter();

  const [query, setQuery] = useState('');

  const counties = data.countyCollection?.items ?? [];

  const filteredCounties =
    query === ''
      ? counties
      : counties.filter((county) => {
          return county?.name?.toLowerCase().includes(query.toLowerCase());
        });

  const getLocationLink = (location: Location) =>
    `/${linkPrefix}/counties/${location?.slug}#plans`;

  return (
    <>
      <Combobox
        as="div"
        onChange={(location: Location) =>
          router.push(getLocationLink(location))
        }
      >
        {({ open }) => (
          <>
            <div className="relative mt-2">
              <Combobox.Button
                as="div"
                onClick={(event: MouseEvent<HTMLDivElement>) => {
                  // Prevent button from firing and preventing clicks on the input when it is already open
                  if (open) event.preventDefault();
                }}
              >
                <Combobox.Input
                  placeholder="County"
                  onChange={(event) => setQuery(event.target.value)}
                  className="w-full rounded-md border-0 bg-white py-4 pl-4 pr-10 text-gray-900 shadow-sm [-webkit-appearance:none] ring-1 ring-inset ring-black placeholder-gray-900 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                />
                <div className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
                  <ChevronDownIcon
                    className="h-5 w-5 text-gray-900"
                    aria-hidden="true"
                  />
                </div>
              </Combobox.Button>

              <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                {filteredCounties.map((county) => (
                  <Combobox.Option
                    key={county?.sys.id}
                    value={county}
                    className={({ active }) =>
                      cx(
                        'relative cursor-default select-none py-2 pl-3 pr-9',
                        active ? 'bg-blue-600 text-white' : 'text-gray-900',
                      )
                    }
                  >
                    <Link href={getLocationLink(county)}>
                      <span className="block truncate">{county?.name}</span>
                    </Link>
                  </Combobox.Option>
                ))}
              </Combobox.Options>
            </div>
          </>
        )}
      </Combobox>
    </>
  );
};
